import React, { useContext, useEffect, useState } from 'react';
import { CartContext } from '../../context/CartContext';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

const headerCellSx = {
  fontSize: '14px',
  color: 'text.secondary',
  fontWeight: 500,
  py: '6px',
  px: '10px',
  bgcolor: 'action.hover',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const bodyCellSx = {
  py: '8px',
  px: '10px',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

function MyCart({ promosByItemCode = {} }) {
  const {
    cart,
    addOneItemToCart,
    removeOneItemFromCart,
    deleteItemFromCart,
    updateDetailsItemInCart,
    fetchCart,
  } = useContext(CartContext);

  // มือถือ: ตารางคอลัมน์ตายตัวเหลือที่ให้ชื่อสินค้าแค่นิดเดียว → แสดงเป็นรายการ 2 บรรทัดแทน
  const isPhone = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [localQty, setLocalQty] = useState({});

  useEffect(() => {
    fetchCart();
  }, []);

  useEffect(() => {
    const qtyMap = {};
    cart.forEach(item => {
      qtyMap[item.roworder] = item.qty;
    });
    setLocalQty(qtyMap);
  }, [cart]);

  const numberWithCommas = (x) =>
    parseFloat(x).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const handleQtyCommit = async (roworder) => {
    const raw = localQty[roworder];
    const newQty = parseInt(raw, 10);
    if (isNaN(newQty) || newQty < 1) {
      const original = cart.find(i => i.roworder === roworder);
      setLocalQty(q => ({ ...q, [roworder]: original ? original.qty : 1 }));
      return;
    }
    await updateDetailsItemInCart({ roworder, qty: newQty });
    fetchCart();
  };

  const handleDelete = async (roworder) => {
    await deleteItemFromCart(roworder);
    fetchCart();
  };

  // ปุ่ม − [จำนวน] + — มือถือปุ่มใหญ่ขึ้นให้แตะง่าย
  const renderStepper = (item) => {
    const btnSize = isPhone ? 32 : 22;
    const btnSx = { width: btnSize, height: btnSize, borderRadius: '50%', border: '0.5px solid', borderColor: 'divider', p: 0 };
    return (
      <Stack direction="row" justifyContent="center" alignItems="center" spacing={isPhone ? 1 : 0.5}>
        <IconButton
          size="small"
          sx={btnSx}
          onClick={async () => {
            await removeOneItemFromCart(item.roworder, item.price);
            fetchCart();
          }}
        >
          <RemoveIcon sx={{ fontSize: isPhone ? 16 : 12 }} />
        </IconButton>
        <TextField
          value={
            localQty[item.roworder] != null && localQty[item.roworder] !== ''
              ? parseFloat(localQty[item.roworder]).toLocaleString('en-US', { maximumFractionDigits: 2 })
              : ''
          }
          onChange={(e) => {
            const onlyDigits = e.target.value.replace(/[^0-9]/g, '');
            setLocalQty(q => ({ ...q, [item.roworder]: onlyDigits }));
          }}
          onBlur={() => handleQtyCommit(item.roworder)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleQtyCommit(item.roworder); }}
          variant="standard"
          size="small"
          inputProps={{
            style: { textAlign: 'center', width: isPhone ? '52px' : '44px', padding: '1px 2px', fontSize: '15px' },
            inputMode: 'numeric',
          }}
          sx={{
            '& .MuiInput-underline:before': { borderBottom: 'none' },
            '& .MuiInput-underline:after': { borderBottom: 'none' },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
          }}
        />
        <IconButton
          size="small"
          sx={btnSx}
          onClick={async () => {
            await addOneItemToCart(item.roworder, item.price);
            fetchCart();
          }}
        >
          <AddIcon sx={{ fontSize: isPhone ? 16 : 12 }} />
        </IconButton>
      </Stack>
    );
  };

  const renderPrice = (item) => (promosByItemCode[item.item_code] ? (
    <Box>
      <Typography sx={{ fontSize: 11, color: 'text.disabled', textDecoration: 'line-through', lineHeight: 1.2 }}>
        {numberWithCommas(parseFloat(item.price))}
      </Typography>
      <Typography sx={{ fontSize: 15, color: '#2e7d32', fontWeight: 500, lineHeight: 1.2 }}>
        {numberWithCommas(Math.max(0, parseFloat(item.price) - promosByItemCode[item.item_code].totalLineDiscount / item.qty))}
      </Typography>
    </Box>
  ) : (
    numberWithCommas(parseFloat(item.price))
  ));

  const renderTotal = (item, sx = {}) => (promosByItemCode[item.item_code] ? (
    <Box>
      <Typography sx={{ fontSize: 11, color: 'text.disabled', textDecoration: 'line-through', lineHeight: 1.2, textAlign: 'right' }}>
        {numberWithCommas(item.qty * item.price)}
      </Typography>
      <Typography sx={{ fontSize: 15, color: '#2e7d32', fontWeight: 500, lineHeight: 1.2, ...sx }}>
        {numberWithCommas(Math.max(0, item.qty * item.price - promosByItemCode[item.item_code].totalLineDiscount))}
      </Typography>
    </Box>
  ) : (
    <Typography sx={{ fontSize: '15px', ...sx }}>
      {numberWithCommas(item.qty * item.price)}
    </Typography>
  ));

  if (isPhone) {
    return (
      <Box>
        {cart.map((item, id) => (
          <Box key={item.roworder} sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
            {/* บรรทัดบน: ลำดับ | ชื่อสินค้าเต็ม + ราคาต่อหน่วย | ลบ */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', minWidth: 18, pt: '3px' }}>{id + 1}</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 500, lineHeight: 1.35, wordBreak: 'break-word' }}>
                  {item.item_name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: '2px', fontSize: 13, color: 'text.secondary' }}>
                  <Box component="span" sx={{ fontSize: 13, color: 'text.primary' }}>{renderPrice(item)}</Box>
                  <span>บาท / {item.unit_code}</span>
                </Box>
              </Box>
              <IconButton size="small" sx={{ p: '4px', mt: '-2px' }} onClick={() => handleDelete(item.roworder)}>
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
            {/* บรรทัดล่าง: จำนวน | รวม */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, pl: '26px' }}>
              {renderStepper(item)}
              {renderTotal(item, { fontSize: 16, fontWeight: 600, textAlign: 'right' })}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer sx={{ px: 1 }}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellSx, width: 32 }}>ลำดับ</TableCell>
            <TableCell sx={headerCellSx}>สินค้า</TableCell>
            <TableCell align="center" sx={{ ...headerCellSx, width: 116 }}>จำนวน</TableCell>
            <TableCell align="right" sx={{ ...headerCellSx, width: 96 }}>ราคา/หน่วย</TableCell>
            <TableCell align="right" sx={{ ...headerCellSx, width: 84 }}>รวม</TableCell>
            <TableCell sx={{ ...headerCellSx, width: 36 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {cart.map((item, id) => (
            <TableRow key={item.roworder} hover>
              <TableCell sx={{ ...bodyCellSx, fontSize: '12px', color: 'text.secondary' }}>
                {id + 1}
              </TableCell>
              <TableCell sx={bodyCellSx}>
                <Box sx={{ fontSize: '15px', lineHeight: 1.3 }}>{item.item_name}</Box>
                <Box sx={{ fontSize: '13px', color: 'text.secondary', mt: '2px' }}>{item.unit_code}</Box>
              </TableCell>
              <TableCell align="center" sx={bodyCellSx}>
                {renderStepper(item)}
              </TableCell>
              <TableCell align="right" sx={bodyCellSx}>
                {renderPrice(item)}
              </TableCell>
              <TableCell align="right" sx={bodyCellSx}>
                {renderTotal(item)}
              </TableCell>
              <TableCell sx={bodyCellSx}>
                <IconButton size="small" sx={{ p: '3px' }} onClick={() => handleDelete(item.roworder)}>
                  <DeleteIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default MyCart;
